import React, { Fragment } from 'react';
import { compose, prop } from 'ramda';
import { I18n } from 'js/components/common/I18n';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { branch, onlyUpdateForKeys, renderNothing, withProps } from 'recompose';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { useGameFeaturesSelector } from 'js/store/hooks/useGameFeaturesSelector';

import gameStudios from './gameStudios';

import style from './index.scss';

export const getIcon = ({ type, value, size = '' }) =>
    `${style[type]} ${typeof value === 'string' ? style[value] : ''} ${size ? style[size] : ''}`;

export const getTitle = ({ t, type, value }) => type === 'volatility'
    ? <I18n tKey={`playtech.mobile.system.featured.${type}.${value}`} />
    : t(`playtech.mobile.system.featured.${type}`);

export const withFeatures = compose(
    withProps(({ code }) => ({ gameFeatures: useGameFeaturesSelector(prop('gameFeatures'))?.[code] })),
    onlyUpdateForKeys(['gameFeatures']),
    withProps(({ gameFeatures, types = [] }) => gameFeatures
        ? { types: types.filter(type => gameFeatures[type]) }
        : {}),
    branch(
        ({ gameFeatures, types }) => !gameFeatures || !types.length,
        renderNothing
    )
);

export const FeatureIcon = ({
    t,
    type,
    size,
    value,
    onClick,
    tabIndex = '-1',
    showTitle = false,
    className = '',
    classNames = {}
}) => {
    const payOutScoreText = type === 'volatility' && tabIndex === '0'
        ? t(`playtech.mobile.system.featured.volatility.aria-label.${value}`)
        : '';

    if (showTitle) {
        return (
            <div className={`${style.itemContainer} ${className}`}>
                <AccessibilityElement
                    onClick={onClick}
                    tabIndex={tabIndex}
                    aria-label={payOutScoreText}
                    className={`${style.icon} ${getIcon({ type, value, size })} ${classNames.image || ''}`}
                />
                <p className={`${style.title} ${classNames.title || ''}`}>
                    {getTitle({ t, type, value })}
                </p>
            </div>
        );
    }

    return type === 'gameStudio'
        ? <img className={className} alt={value} src={gameStudios[value]} />
        : <AccessibilityElement
            onClick={onClick}
            tabIndex={tabIndex}
            aria-label={payOutScoreText}
            className={`${style.icon} ${getIcon({ type, value, size })} ${className}`}
        />;
};

export const FeatureIcons = ({ gameFeatures, types, className = '', classNames = {}, ...other }) => (
    <Fragment>
        {types.map(type => <FeatureIcon type={type}
            value={gameFeatures[type]}
            key={type}
            className={`${classNames.item || ''} ${className}`}
            classNames={classNames}
            {...other} />
        )}
    </Fragment>
);

export const withContainer = Wrapped => ({ className = '', ...props }) => (
    <div className={className}>
        <Wrapped {...props} />
    </div>
);

export default compose(
    withFeatures,
    withTranslations,
    branch(
        props => !props.noContainer,
        withContainer
    )
)(FeatureIcons);
